import styled from '@emotion/styled';
import { debounce } from 'lodash';
import { FC, useEffect, useRef, ReactNode, Fragment } from 'react';

import Divider from '@/assets/animations/divider.svg';

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-wrap: wrap;
`;

const Loader = styled.div`
  position: absolute;
  bottom: -30px;
  width: 100%;
  height: 20px;
`;

interface IInfiniteScrollProps {
  hasMore: boolean;
  items: Array<any>;
  loading: boolean;
  containerRef?: HTMLDivElement;
  loaderElement?: ReactNode;
  children: (props) => ReactNode;
  onLoadMore: () => void;
}

export const InfiniteScroll: FC<IInfiniteScrollProps> = ({
  items,
  children,
  loading,
  hasMore,
  containerRef,
  loaderElement,
  onLoadMore,
  ...restProps
}) => {
  const scrollContainer = useRef();
  const lastScrollValue = useRef(null);

  const loadMore = () => {
    if (!scrollContainer.current) {
      return false;
    }
    // @ts-ignore: Object is possibly 'null'.
    const bounding = scrollContainer.current.getBoundingClientRect();

    if (bounding.bottom <= (window.innerHeight || document.documentElement.clientHeight) && hasMore) {
      onLoadMore();
    }
  };

  useEffect(() => {
    const tempContainerRef = containerRef || window;

    const handleScroll = debounce(() => {
      if (!scrollContainer.current) {
        return false;
      }

      // @ts-ignore: Object is possibly 'null'.
      const bounding = scrollContainer.current.getBoundingClientRect();

      if ((!loading && lastScrollValue.current === null) || bounding.top < lastScrollValue.current) {
        loadMore();
      }
      lastScrollValue.current = bounding.top;
    }, 150);

    tempContainerRef.addEventListener('scroll', handleScroll);

    return () => {
      tempContainerRef.removeEventListener('scroll', handleScroll);
    };
  });

  return (
    <Wrapper {...restProps} ref={scrollContainer}>
      {items && items.map((item) => children(item))}
      {loading && (
        <Fragment>
          {loaderElement || (
            <Loader>
              <Divider />
            </Loader>
          )}
        </Fragment>
      )}
    </Wrapper>
  );
};
